import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { AssignmentOutlined, ShieldOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import { DocumentList, ExpProcessRow, PaperConfigMissing, StepActionsExpediente } from "@components/ui_v2";
import PersonFormTabs from "@components/PersonForm/PersonFormTabs";
import { RootState, useAppDispatch } from "@store/store";
import { BofuTabDocument } from "@interfaces/response";
import {
  applyPersonDocumentChange,
  computePersonsIsComplete,
  PersonDocumentChange,
} from "@components/PersonForm/personHelpers";
import { setExpedientePersonas } from "@store/slices/expedienteSlice";
import { ALTO_SKELETON_EXPEDIENTE, BOFU_TABS_CONFIG, PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";
import { isFormStatusEditable } from "@helpers/funciones";

const tieneArchivo = (doc: BofuTabDocument) => (doc.multiple ? doc.list_values.some(Boolean) : Boolean(doc.file_value));

export const TabPersonas = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const expedientePersonas = useSelector((state: RootState) => state.expediente.personas);
  const tabsReady = useSelector((state: RootState) => state.expediente.tabsReady);

  const persons = expedientePersonas.persons;
  const isLoading = !tabsReady;
  const isTabConfigMissing = tabsReady && !expedientePersonas.is_active;

  const [documentsByPerson, setDocumentsByPerson] = useState<Record<string, BofuTabDocument[]>>(
    expedientePersonas.documentsByPerson,
  );
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(persons[0]?.id || null);

  useEffect(() => {
    if (tabsReady) {
      setDocumentsByPerson(expedientePersonas.documentsByPerson);
      setSelectedPersonId((current) => {
        if (current && expedientePersonas.persons.some((p) => p.id === current)) return current;
        return expedientePersonas.persons[0]?.id || null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsReady]);

  const selectedPerson = useMemo(
    () => persons.find((p) => p.id === selectedPersonId) || null,
    [persons, selectedPersonId],
  );

  const selectedDocuments = selectedPersonId ? documentsByPerson[selectedPersonId] || [] : [];

  const handleDocumentChange = (change: PersonDocumentChange) => {
    if (!selectedPersonId) return;
    setDocumentsByPerson((current) => {
      const updated = {
        ...current,
        [selectedPersonId]: applyPersonDocumentChange(current[selectedPersonId] || [], change),
      };
      const updatedPersons = computePersonsIsComplete(persons, updated);
      dispatch(setExpedientePersonas({ ...expedientePersonas, persons: updatedPersons }));
      return updated;
    });
  };

  const handleOpenPersonalForm = () => {
    if (!selectedPersonId) return;
    navigate(`${PATHS_EXPEDIENTE_V2.formularioPersona.path}/${selectedPersonId}`);
  };

  const handleOpenIdentityValidation = () => {
    if (!selectedPersonId) return;
    navigate(`${PATHS_EXPEDIENTE_V2.validacionIdentidad.path}/${selectedPersonId}`);
  };

  const handleOpenBuroForm = () => {
    if (!selectedPersonId) return;
    navigate(`${PATHS_EXPEDIENTE_V2.consultaBuroPf.path}/${selectedPersonId}`);
  };

  const hasNoContent =
    !persons.some((p) => p.showPersonalForm.enabled || p.showIdentityValidation.enabled || p.showBuroForm.enabled) &&
    selectedDocuments.length === 0;

  const todosCompletos = useMemo(
    () =>
      persons.length > 0 &&
      Object.values(documentsByPerson).every((docs) => docs.filter((d) => d.required).every(tieneArchivo)) &&
      persons.every((person) => {
        if (person.showPersonalForm.enabled && person.personal_information_status !== "COMPLETO") return false;
        if (person.showIdentityValidation.enabled && person.identity_validation_completed !== true) return false;
        if (
          person.showBuroForm.enabled &&
          !(person.is_credit_bureau_authorization_pf_sended && person.is_credit_bureau_authorization_pf_correct)
        )
          return false;
        return true;
      }),
    [persons, documentsByPerson],
  );

  return (
    <Box>
      {isLoading ? (
        <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON_EXPEDIENTE, borderRadius: 2 }} />
      ) : isTabConfigMissing ? (
        <PaperConfigMissing />
      ) : persons.length === 0 ? (
        <Typography color="primary.dark" fontSize={16} textAlign="center" fontWeight={600} mt={3}>
          No se encontraron personas registradas
        </Typography>
      ) : hasNoContent ? (
        <PaperConfigMissing />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          <PersonFormTabs persons={persons} selectedPersonId={selectedPersonId} onSelectPerson={setSelectedPersonId} />

          {selectedPerson && (
            <Fragment>
              {selectedPerson?.showPersonalForm.enabled && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Información personal"
                    title="Llena tu información personal"
                    hint="Datos de contacto, domicilio y situación laboral"
                    status={selectedPerson.personal_information_status ?? "PENDIENTE"}
                    icon={<AssignmentOutlined sx={{ fontSize: 18 }} />}
                    label="Llenar"
                    onClick={handleOpenPersonalForm}
                    disabled={!isFormStatusEditable(selectedPerson.personal_information_status ?? "PENDIENTE")}
                    data-testid="expediente-personas-form-button"
                  />
                </Paper>
              )}

              {selectedPerson?.showIdentityValidation.enabled && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Verifica tu identidad"
                    hint="Selfie con prueba de vida + identificación oficial"
                    status={selectedPerson.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE"}
                    icon={<ShieldOutlined sx={{ fontSize: 18 }} />}
                    label="Validar"
                    onClick={handleOpenIdentityValidation}
                    disabled={
                      !isFormStatusEditable(
                        selectedPerson.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE",
                      )
                    }
                    data-testid="expediente-personas-identidad-button"
                  />
                </Paper>
              )}

              {selectedPerson?.showBuroForm.enabled && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Buró de crédito"
                    title="Autoriza la consulta al buró de crédito"
                    hint="Verificamos tu historial crediticio con un código SMS"
                    status={
                      selectedPerson.is_credit_bureau_authorization_pf_sended &&
                      selectedPerson.is_credit_bureau_authorization_pf_correct
                        ? "COMPLETO"
                        : "PENDIENTE"
                    }
                    icon={<VerifiedUserOutlined sx={{ fontSize: 18 }} />}
                    label="Autorizar"
                    onClick={handleOpenBuroForm}
                    disabled={
                      !isFormStatusEditable(
                        selectedPerson.is_credit_bureau_authorization_pf_sended &&
                          selectedPerson.is_credit_bureau_authorization_pf_correct
                          ? "COMPLETO"
                          : "PENDIENTE",
                      )
                    }
                    data-testid="expediente-personas-buro-button"
                  />
                </Paper>
              )}

              {selectedDocuments.length > 0 && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <DocumentList
                    key={selectedPerson.id}
                    documents={selectedDocuments}
                    onChange={handleDocumentChange}
                    onChangeMultiple={handleDocumentChange}
                  />
                </Paper>
              )}
            </Fragment>
          )}

          <StepActionsExpediente
            currentTab={BOFU_TABS_CONFIG.tab_personas.code}
            nextDisabled={!todosCompletos}
            showRevision
          />
        </Box>
      )}
    </Box>
  );
};
